import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, RotateCcw, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Num } from "@/components/Num";
import { MONTHS } from "@/lib/format";

const round4 = (v) => Math.round(v * 10000) / 10000;

export const OverrideDialog = ({ open, onOpenChange, line, budget, target: initialTarget, onSave, busy, lockedBudget, rollingLine, rollingLastMonth = 0 }) => {
  const [target, setTarget] = useState(initialTarget || "forecast");
  const [mode, setMode] = useState("pct");
  const [inputs, setInputs] = useState({});
  const [fill, setFill] = useState("");
  const lastM = budget?.last_actual_month || 0;
  const isForecast = target === "forecast";
  const isRolling = target === "rolling";
  const year = isForecast ? budget?.actual_year : budget?.budget_year;
  const system = useMemo(() => {
    if (!line) return [];
    if (isRolling) {
      const r = rollingLine || {};
      return MONTHS.map((_, i) => ({ base: r.forecast?.[i], current: r.forecast?.[i], overridden: !!r.overrides && r.overrides[String(i + 1)] !== undefined, disabled: i < rollingLastMonth }));
    }
    const cur = isForecast ? line.fy : line.budget;
    const base = isForecast ? line.fy_base : line.budget_base;
    const ov = isForecast ? line.fy_overrides : line.overrides;
    return MONTHS.map((_, i) => ({ base: base ? base[i] : cur?.[i], current: cur?.[i], overridden: !!ov && ov[String(i + 1)] !== undefined, disabled: isForecast && i < lastM }));
  }, [line, isForecast, lastM, isRolling, rollingLine, rollingLastMonth]);

  useEffect(() => {
    if (!open) return;
    setTarget(initialTarget || "forecast");
    setInputs({});
    setFill("");
  }, [open, initialTarget, line?.id]);

  const result = (i) => {
    const s = system[i];
    const raw = inputs[i];
    if (raw === undefined || raw === "") return s?.overridden ? s.current : null;
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (mode === "pct") return s.base === null || s.base === undefined ? null : s.base * (1 + n / 100);
    return n;
  };
  const applyFill = () => {
    if (fill === "") return;
    const next = {};
    system.forEach((s, i) => { if (!s.disabled) next[i] = fill; });
    setInputs(next);
  };
  const save = () => {
    const values = {};
    system.forEach((s, i) => {
      if (s.disabled) return;
      const raw = inputs[i];
      if (raw === undefined || raw === "") return;
      const r = result(i);
      if (r !== null) values[String(i + 1)] = round4(r);
    });
    if (!Object.keys(values).length) { onOpenChange(false); return; }
    onSave(target, line.id, values);
  };
  const reset = () => {
    const values = {};
    system.forEach((s, i) => { if (s.overridden && !s.disabled) values[String(i + 1)] = null; });
    if (Object.keys(values).length) onSave(target, line.id, values);
  };
  const readOnly = target === "budget" && lockedBudget;
  const histCols = useMemo(() => {
    const cols = [];
    const h = line?.hist || {};
    Object.keys(h).sort().forEach((y) => cols.push({ label: `Act ${y}`, arr: h[y] }));
    if (Array.isArray(line?.ly) && line.ly.some((v) => v !== null && v !== undefined) && !cols.some((c) => c.label === `Act ${(budget?.actual_year || 0) - 1}`)) cols.push({ label: `Act ${(budget?.actual_year || 0) - 1}`, arr: line.ly });
    if (!isForecast && Array.isArray(line?.fy)) cols.push({ label: `${lastM >= 12 ? "Act" : "Act/Est"} ${budget?.actual_year}`, arr: line.fy });
    return cols.slice(-3);
  }, [line, budget, isForecast, lastM]);
  const nOverridden = system.filter((s) => s.overridden).length;
  if (!line) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="override-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading inline-flex items-center gap-2"><SlidersHorizontal size={16} className="text-emerald-700" /> Override — {line.label}</DialogTitle>
          <DialogDescription>Enter a change per month as a percentage of the system value or as a nominal amount. Blank months keep the system value; existing overrides are shown in green.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid="override-target-toggle">
            <button type="button" onClick={() => { setTarget("forecast"); setInputs({}); }} data-testid="override-target-forecast" className={`px-3 py-1.5 rounded ${isForecast ? "bg-slate-900 text-white" : "text-slate-600"}`}>Forecast {budget?.actual_year}</button>
            <button type="button" onClick={() => { setTarget("budget"); setInputs({}); }} data-testid="override-target-budget" className={`px-3 py-1.5 rounded ${target === "budget" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Budget {budget?.budget_year}</button>
            {rollingLine && <button type="button" onClick={() => { setTarget("rolling"); setInputs({}); }} data-testid="override-target-rolling" className={`px-3 py-1.5 rounded ${isRolling ? "bg-emerald-700 text-white" : "text-slate-600"}`}>Forecast {budget?.budget_year}</button>}
          </div>
          <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid="override-mode-toggle">
            <button type="button" onClick={() => setMode("pct")} data-testid="override-mode-pct" className={`px-3 py-1.5 rounded ${mode === "pct" ? "bg-emerald-700 text-white" : "text-slate-600"}`}>% change</button>
            <button type="button" onClick={() => setMode("abs")} data-testid="override-mode-abs" className={`px-3 py-1.5 rounded ${mode === "abs" ? "bg-emerald-700 text-white" : "text-slate-600"}`}>Nominal</button>
          </div>
        </div>
        {isRolling && <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="override-rolling-note">Forecast {budget?.budget_year} overrides change the rolling forecast only — the approved budget stays exactly as generated. Update after each revenue meeting.</p>}
        {readOnly && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="override-locked-note">Budget is locked (submitted/approved) — budget overrides are disabled. Forecast overrides remain available.</p>}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Apply to all open months:</span>
          <Input type="number" value={fill} onChange={(e) => setFill(e.target.value)} placeholder={mode === "pct" ? "e.g. 5" : "amount"} className="h-8 w-32 font-mono" data-testid="override-fill-input" disabled={readOnly} />
          <Button type="button" size="sm" variant="outline" onClick={applyFill} className="h-8" data-testid="override-fill-apply" disabled={readOnly}>Fill</Button>
        </div>
        <div className="max-h-[50vh] overflow-auto rounded border border-slate-100">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="py-1.5 px-2 text-left">Month</th>{histCols.map((c) => <th key={c.label} className="py-1.5 px-2 text-right text-slate-400" data-testid={`override-hist-col-${c.label.replace(/[^0-9]/g, "")}`}>{c.label}</th>)}<th className="py-1.5 px-2 text-right">System value</th><th className="py-1.5 px-2 text-right w-32">{mode === "pct" ? "Change %" : "New value"}</th><th className="py-1.5 px-2 text-right">Result</th></tr>
            </thead>
            <tbody>
              {system.map((s, i) => {
                const r = result(i);
                return (
                  <tr key={i} className={`border-t border-slate-100 ${s.disabled ? "opacity-50" : ""}`} data-testid={`override-row-${i + 1}`}>
                    <td className="py-1 px-2 font-medium text-slate-700">{MONTHS[i]} {String(year).slice(2)}{s.disabled && <span className="ml-1 text-[10px] text-slate-400">actual</span>}</td>
                    {histCols.map((c) => <td key={c.label} className="py-1 px-2 text-right font-mono text-slate-400"><Num v={c.arr?.[i]} line={line} /></td>)}
                    <td className="py-1 px-2 text-right font-mono text-slate-600"><Num v={s.base} line={line} /></td>
                    <td className="py-1 px-2 text-right">
                      <Input type="number" value={inputs[i] ?? ""} disabled={s.disabled || readOnly} onChange={(e) => setInputs({ ...inputs, [i]: e.target.value })} className="h-7 font-mono text-right text-xs" data-testid={`override-input-${i + 1}`} />
                    </td>
                    <td className={`py-1 px-2 text-right font-mono ${r !== null && (inputs[i] ?? "") !== "" ? "text-emerald-800 font-semibold" : s.overridden ? "text-emerald-700" : "text-slate-400"}`} data-testid={`override-result-${i + 1}`}>
                      {r === null ? "—" : <Num v={r} line={line} />}{s.overridden && (inputs[i] ?? "") === "" && <span className="ml-1 text-[10px] text-emerald-600">override</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={busy || readOnly || !nOverridden} data-testid="override-reset-line" className="text-red-600 hover:text-red-700"><RotateCcw size={13} className="mr-1" /> Reset {nOverridden ? `${nOverridden} override(s)` : ""}</Button>
          <Button type="button" onClick={save} disabled={busy || readOnly} data-testid="override-save" className="bg-slate-900 hover:bg-emerald-700 text-white"><Save size={14} className="mr-2" /> {busy ? "Saving…" : "Save overrides"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
